import React from 'react'
import { Button, Group, Stack } from '@mantine/core'
import {
  CalendarIcon,
  ChatBubbleIcon,
  ClockIcon,
  FrameIcon,
  GlobeIcon,
  HomeIcon,
  PersonIcon,
  ReaderIcon,
  SewingPinIcon,
} from '@radix-ui/react-icons'

import { CourtReporterPageEvent } from './types'

import {
  CourtReporterEventBadges,
  formatOnSiteLocation,
} from '@/components/Calendar/CourtReporterCalendarEvent'
import { DataEntry } from '@/components/DataEntry'
import { ConnectionInformationButton } from '@/components/Event/ConnectionInformationButton'
import { Card } from '@/components/Event/Detail/components/Card'
import { useFilesModal } from '@/components/Event/hooks/useFilesModal'
import { DepositionNoticesModal } from '@/components/Event/Modals/DepositionNoticesModal'
import { ExhibitsModal } from '@/components/Event/Modals/ExhibitsModal'
import {
  formatDate,
  formatDuration,
  formatTimeRange,
  formatTimezone,
  getEventTimezone,
} from '@/utils/time'

export const DetailsCard: React.FC<{
  event: CourtReporterPageEvent
}> = ({ event }) => {
  const openExhibitsModal = useFilesModal(event, 'Exhibits', ExhibitsModal)
  const openDepositionNoticesModal = useFilesModal(
    event,
    'Deposition notice',
    DepositionNoticesModal,
  )
  const location = formatOnSiteLocation(event)
  const tz = getEventTimezone(event)

  return (
    <Card heading={event.name}>
      <Stack spacing="md">
        <CourtReporterEventBadges
          event={event}
          status={
            'general_status' in event
              ? event.general_status
              : event.court_reporter_status
          }
        />

        <Stack ml="xs">
          <Stack spacing="xs">
            <DataEntry icon={CalendarIcon} tooltip="Date">
              {formatDate(event.datetime, { long: true, separator: ' ', tz })}
            </DataEntry>

            <DataEntry icon={ClockIcon} tooltip="Time">
              {formatTimeRange(event.datetime, event.duration, { tz })}{' '}
              {formatTimezone(tz, event.datetime)} (
              {formatDuration(event.duration)})
            </DataEntry>

            {'general_status' in event && (
              <DataEntry icon={PersonIcon} tooltip="Court reporter">
                {event.court_reporter_booking?.court_reporter_name ??
                  'Unassigned'}
              </DataEntry>
            )}

            {location && (
              <DataEntry icon={SewingPinIcon} tooltip="Address">
                {location}
              </DataEntry>
            )}

            <DataEntry icon={HomeIcon} tooltip="Organization">
              {event.team.organization.name}: {event.team.name}
            </DataEntry>
          </Stack>

          <Stack spacing="xs">
            <DataEntry
              icon={ReaderIcon}
              tooltip={event.case.is_claim ? 'Claim' : 'Case'}
            >
              {event.case.name}
            </DataEntry>

            {(event.case.number || event.claim_number) && (
              <DataEntry
                icon={FrameIcon}
                tooltip={event.case.number ? 'Case number' : 'Claim number'}
              >
                {event.case.number || event.claim_number}
              </DataEntry>
            )}

            {event.team.state && event.team.city && (
              <DataEntry icon={GlobeIcon} tooltip="Client location">
                {event.team.state}: {event.team.city}
              </DataEntry>
            )}
          </Stack>

          {event.recording.is_translator_present && (
            <Stack spacing="xs">
              <DataEntry icon={ChatBubbleIcon} tooltip="Interpreter">
                Interpreter present
              </DataEntry>
            </Stack>
          )}
        </Stack>

        {/* In-Event Support joins these once the CR HelpScout page exists. */}
        <Group spacing={4}>
          {event.court_reporter_status !== 'cancelled' && (
            <Button variant="light" color="gray" onClick={openExhibitsModal}>
              Exhibits
            </Button>
          )}
          <Button
            variant="light"
            color="gray"
            onClick={openDepositionNoticesModal}
          >
            Notice
          </Button>
          {event.court_reporter_status === 'booked' && (
            <ConnectionInformationButton event={event} />
          )}
        </Group>
      </Stack>
    </Card>
  )
}
